import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Space, Typography, Steps, Result, Empty } from 'antd';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { useHistory } from 'react-router-dom';
import { CheckCircleOutlined, ExclamationOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const FirstLoginWelcomeModal = () => {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const history = useHistory();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Check if this is first login or profile is incomplete
        // Use a flag in localStorage to track if we've shown the modal
        const hasSeenWelcomeModal = localStorage.getItem(`welcomeModal_${currentAdmin?._id}`);
        const isIncomplete = currentAdmin?.profileStatus === 'Incomplete' || !currentAdmin?.profileStatus;

        // Show modal if:
        // 1. Profile is incomplete AND
        // 2. We haven't shown the modal before for this user
        if (isIncomplete && !hasSeenWelcomeModal && currentAdmin?.role !== 'super_admin') {
            setVisible(true);
            // Mark that we've shown the modal
            localStorage.setItem(`welcomeModal_${currentAdmin?._id}`, 'true');
        }
    }, [currentAdmin?._id, currentAdmin?.profileStatus, currentAdmin?.role]);

    const handleCompleteProfile = () => {
        setVisible(false);
        history.push('/my-profile');
    };

    if (currentAdmin?.profileStatus === 'Verified' || currentAdmin?.role === 'super_admin') {
        return null;
    }

    return (
        <Modal
            title={null}
            open={visible}
            onCancel={() => { }} // Prevent closing by clicking X or outside
            footer={null}
            closable={false}
            maskClosable={false}
            width={600}
            centered
            bodyStyle={{ padding: '40px 24px' }}
        >
            <div style={{ textAlign: 'center' }}>
                <ExclamationOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />

                <Title level={2} style={{ marginBottom: 8 }}>
                    Welcome! 👋
                </Title>

                <Paragraph type="secondary" style={{ fontSize: 16, marginBottom: 24 }}>
                    Your profile needs to be completed before you can access full system features.
                </Paragraph>

                <Card
                    style={{
                        background: '#fef8e6',
                        border: '1px solid #ffe58f',
                        marginBottom: 24,
                        borderRadius: 8
                    }}
                >
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <div>
                            <Text strong style={{ color: '#d46b08' }}>📋 What needs to be completed:</Text>
                        </div>

                        <div style={{ textAlign: 'left' }}>
                            <Steps
                                direction="vertical"
                                current={3}
                                status="process"
                                items={[
                                    {
                                        title: 'Personal Information',
                                        description: 'Your basic details and contact information',
                                    },
                                    {
                                        title: 'Employment Acknowledgement',
                                        description: 'Review and confirm your employment terms',
                                    },
                                    {
                                        title: 'Document Upload',
                                        description: 'Submit required documents (CNIC, resume, etc.)',
                                    },
                                ]}
                            />
                        </div>

                        <div style={{
                            background: '#e6f7ff',
                            padding: 12,
                            borderRadius: 6,
                            textAlign: 'left',
                            border: '1px solid #91d5ff'
                        }}>
                            <Text style={{ color: '#0050b3' }}>
                                ⏱️ <strong>Estimated time:</strong> 5-10 minutes to complete
                            </Text>
                        </div>
                    </Space>
                </Card>

                <div style={{
                    background: '#f6f6f6',
                    padding: 16,
                    borderRadius: 8,
                    marginBottom: 24,
                    textAlign: 'left'
                }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        <strong>Note:</strong> Your salary processing is on hold until your profile is verified by HR.
                        Complete your profile now to expedite the process.
                    </Text>
                </div>

                <Button
                    type="primary"
                    size="large"
                    style={{
                        width: '100%',
                        height: 44,
                        fontSize: 16,
                        fontWeight: 600,
                        borderRadius: 6
                    }}
                    onClick={handleCompleteProfile}
                >
                    ✓ Complete My Profile
                </Button>

                <Button
                    type="text"
                    style={{
                        width: '100%',
                        marginTop: 8,
                        color: '#595959',
                        fontSize: '14px',
                        fontWeight: 500
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#262626'}
                    onMouseLeave={(e) => e.target.style.color = '#595959'}
                    onClick={() => setVisible(false)}
                >
                    Maybe Later
                </Button>

                <Paragraph type="secondary" style={{ marginTop: 16, fontSize: 12 }}>
                    Your profile verification is required for full system access.
                </Paragraph>
            </div>
        </Modal>
    );
};

export default FirstLoginWelcomeModal;
